// Angular
import {
  Component,
  OnInit,
  ViewChild,
  Input,
  ChangeDetectionStrategy,
  OnDestroy,
} from '@angular/core';
// Material
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
// RXJS
import { skip, tap } from 'rxjs/operators';
import { merge, Subscription, Observable } from 'rxjs';
// NGRX
import { Store, select } from '@ngrx/store';
import { AppState } from '../../../../../../../../core/reducers';
// CRUD
import {
  QueryParamsModel,
  LayoutUtilsService,
  MessageType,
} from '../../../../../../../../core/_base/crud';
// Services and models
import {
  PharmacyGroupDrug,
  AddPharmacyGroupDrugs,
  PharmacyGroupDrugsDataSource,
  selectPharmacyGroupDrugError,
  PharmacyGroupDrugStoreUpdated,
  PharmacyGroupDrugsPageRequested,
  PharmacyGroupDrugOnServerDeleted,
  PharmacyGroupDrugOnServerUpdated,
  selectPharmacyGroupDrugLatestSuccessfullAction,
  PharmacyGroupDrugActionTypes,
  ClearPharmacyGroupDrugState,
} from '../../../../../../../../core/lists';
// Components
import { PharmacyGroupDrugEditDialogComponent } from '../drug-edit/drug-edit-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { Validators, FormBuilder, FormGroup } from '@angular/forms';
import { Update } from '@ngrx/entity';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-pharmacy-group-drugs-list',
  templateUrl: './drugs-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PharmacyGroupDrugsListComponent implements OnInit, OnDestroy {
  // Public properties

  // Incoming data
  @Input() pharmacyGroupId$: Observable<number>;
  @Input() isViewMode$: Observable<boolean>;
  pharmacyGroupId: number;
  isViewMode: boolean;

  // Table fields
  dataSource: PharmacyGroupDrugsDataSource;
  displayedColumns = ['drug', 'ndc', 'price', 'description', 'is_active'];
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  // Selection
  selection = new SelectionModel<PharmacyGroupDrug>(true, []);
  pharmacyGroupDrugsResult: PharmacyGroupDrug[] = [];
  drugTypes = [];
  // Edit
  isSwitchedToEditMode = false;
  loadingAfterSubmit = false;
  formGroup: FormGroup;
  drugForEdit: PharmacyGroupDrug;

  // Private properties
  private subscriptions: Subscription[] = [];

  /**
   * Component constructor
   *
   * @param store: Store<AppState></AppState>
   * @param fb: FormBuilder
   * @param dialog: MatDialog
   * @param translate: TranslateService
   * @param layoutUtilsService: LayoutUtilsService
   * @param pharmacyGroupDrugService: PharmacyGroupDrugService
   */
  constructor(
    private store: Store<AppState>,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private translate: TranslateService,
    private layoutUtilsService: LayoutUtilsService,
  ) {}

  /**
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */
  ngOnInit() {
    // If the user changes the sort order, reset back to the first page.
    const sortSub = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.subscriptions.push(sortSub);

    /* Data load will be triggered in two cases:
		- when a pagination event occurs => this.paginator.page
		- when a sort event occurs => this.sort.sortChange
		**/
    const loadSub = merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        tap(() => {
          this.loadDrugsList();
        }),
      )
      .subscribe();
    this.subscriptions.push(loadSub);

    // Init DataSource
    this.dataSource = new PharmacyGroupDrugsDataSource(this.store);
    const dataSourceSub = this.dataSource.entitySubject.subscribe(
      (res) => (this.pharmacyGroupDrugsResult = res),
    );
    this.subscriptions.push(dataSourceSub);
    const cSub = this.pharmacyGroupId$.subscribe((res) => {
      if (!res) {
        return;
      }

      this.pharmacyGroupId = res;
      this.loadDrugsList();
      this.createFormGroup();
    });
    this.subscriptions.push(cSub);
    const vSub = this.isViewMode$.subscribe((res) => {
      this.isViewMode = res;
      // tslint:disable-next-line:max-line-length
      this.displayedColumns = this.isViewMode
        ? ['drug', 'ndc', 'price', 'is_active']
        : ['drug', 'ndc', 'price', 'is_active', 'actions'];
    });
    this.subscriptions.push(vSub);
    const errorSubscription = this.store
      .pipe(select(selectPharmacyGroupDrugError), skip(1))
      .subscribe((res) => {
        if (res) {
          this.layoutUtilsService.showActionNotification(
            this.translate.instant('GLOBAL.UNSPECIFIED_ERROR'),
            MessageType.Delete,
            3000,
            true,
            false,
          );
        }
      });
    this.subscriptions.push(errorSubscription);
    const successSubscription = this.store
      .pipe(select(selectPharmacyGroupDrugLatestSuccessfullAction), skip(1))
      .subscribe((res) => {
        if (res && res.action_type) {
          let successMsg;
          let msgType;
          switch (res.action_type) {
            case PharmacyGroupDrugActionTypes.AddPharmacyGroupDrugs:
              this.loadDrugsList();
              successMsg = this.translate.instant('PHARMACY_GROUP_DRUG.EDIT.ADD_MESSAGE');
              msgType = MessageType.Create;
              break;
            case PharmacyGroupDrugActionTypes.PharmacyGroupDrugUpdated:
              successMsg = this.translate.instant('PHARMACY_GROUP_DRUG.EDIT.UPDATE_MESSAGE');
              msgType = MessageType.Update;
              break;
            case PharmacyGroupDrugActionTypes.PharmacyGroupDrugDeleted:
              successMsg = this.translate.instant(
                'PHARMACY_GROUP_DRUG.DELETE_PHARMACY_GROUP_DRUG_SIMPLE.MESSAGE',
              );
              msgType = MessageType.Delete;
              break;
          }
          if (successMsg) {
            this.layoutUtilsService.showActionNotification(successMsg, msgType, 3000, true, false);
          }
        }
      });
    this.subscriptions.push(successSubscription);
  }

  /**
   * On destroy
   */
  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.store.dispatch(new ClearPharmacyGroupDrugState());
  }

  /**
   * Load Drugs List
   */
  loadDrugsList() {
    this.selection.clear();
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction !== '' ? this.sort.direction : 'desc',
      !this.sort.active || this.sort.active === '' ? 'created' : this.sort.active,
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );
    // Call request from server
    this.store.dispatch(
      new PharmacyGroupDrugsPageRequested({
        page: queryParams,
      }),
    );
  }

  /**
   * Retirns object for filter
   */
  filterConfiguration(): any {
    const filter: any = {};
    filter.pharmacyGroupId = this.pharmacyGroupId;
    return filter;
  }

  /**
   * Check all rows are selected
   */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.pharmacyGroupDrugsResult.length;
    return numSelected === numRows;
  }

  /**
   * Selects all rows if they are not all selected; otherwise clear selection
   */
  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.pharmacyGroupDrugsResult.forEach((row) => this.selection.select(row));
    }
  }

  /** ACTIONS */
  /**
   * Delete drug
   *
   * @param _item: PharmacyGroupDrug
   */
  deleteDrug(_item: PharmacyGroupDrug) {
    const _title: string = this.translate.instant(
      'PHARMACY_GROUP_DRUG.DELETE_PHARMACY_GROUP_DRUG_SIMPLE.TITLE',
    );
    const _description: string = this.translate.instant(
      'PHARMACY_GROUP_DRUG.DELETE_PHARMACY_GROUP_DRUG_SIMPLE.DESCRIPTION',
    );
    const _waitDesciption: string = this.translate.instant(
      'PHARMACY_GROUP_DRUG.DELETE_PHARMACY_GROUP_DRUG_SIMPLE.WAIT_DESCRIPTION',
    );

    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDesciption);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }

      this.store.dispatch(
        new PharmacyGroupDrugOnServerDeleted({
          id: _item.id,
          pharmacyGroupId: this.pharmacyGroupId,
        }),
      );
    });
  }

  /**
   * Open add drug dialog and save data
   */
  addDrugs() {
    const dialogRef = this.dialog.open(PharmacyGroupDrugEditDialogComponent, {
      width: '35vw',
      data: { drugs: this.pharmacyGroupDrugsResult },
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res && res.drugs) {
        this.store.dispatch(
          new AddPharmacyGroupDrugs({ drugs: res.drugs, pharmacyGroupId: this.pharmacyGroupId }),
        );
      }
    });
  }

  createFormGroup() {
    this.formGroup = this.fb.group({
      editPrice: [0, Validators.compose([Validators.required])],
      editIsActive: [false, Validators.compose([Validators.required])],
    });
    this.clearEditForm();
  }

  clearEditForm() {
    const controls = this.formGroup.controls;
    controls.editPrice.setValue(0);
    controls.editPrice.markAsPristine();
    controls.editPrice.markAsUntouched();
    controls.editIsActive.setValue(false);
    controls.editIsActive.markAsPristine();
    controls.editIsActive.markAsUntouched();
    this.drugForEdit = new PharmacyGroupDrug();
    this.drugForEdit._isEditMode = false;
  }

  /**
   * Update drug
   *
   * @param _item: PharmacyGroupDrug
   */
  editDrugButtonOnClick(_item: PharmacyGroupDrug) {
    const controls = this.formGroup.controls;
    controls.editPrice.setValue(_item.price);
    controls.editIsActive.setValue(_item.is_active);
    const updatePharmacyGroupDrug: Update<PharmacyGroupDrug> = {
      id: _item.id,
      changes: {
        _isEditMode: true,
      },
    };
    this.store.dispatch(
      new PharmacyGroupDrugStoreUpdated({ pharmacyGroupDrug: updatePharmacyGroupDrug }),
    );
    this.isSwitchedToEditMode = true;
  }

  /**
   * Cancel drug
   *
   * @param _item: PharmacyGroupDrug
   */
  cancelEditButtonOnClick(_item: PharmacyGroupDrug) {
    const updatePharmacyGroupDrug: Update<PharmacyGroupDrug> = {
      id: _item.id,
      changes: {
        _isEditMode: false,
      },
    };
    this.store.dispatch(
      new PharmacyGroupDrugStoreUpdated({ pharmacyGroupDrug: updatePharmacyGroupDrug }),
    );
    this.isSwitchedToEditMode = false;
  }

  /**
   * Check is Edit Form valid
   */
  checkEditForm() {
    const controls = this.formGroup.controls;
    if (controls.editPrice.invalid) {
      controls.editPrice.markAsTouched();
      return false;
    }
    return true;
  }

  /**
   * Edit drug dialog and save data
   *
   * @param item: PharmacyGroupDrug
   */
  editDrug(item: PharmacyGroupDrug) {
    if (!this.checkEditForm()) {
      return;
    }

    this.loadingAfterSubmit = true;
    const controls = this.formGroup.controls;
    const objectForUpdate = new PharmacyGroupDrug();
    objectForUpdate.id = item.id;
    objectForUpdate.drug_name = item.drug_name;
    objectForUpdate.drug_ndc = item.drug_ndc;
    objectForUpdate.price = controls.editPrice.value;
    objectForUpdate.is_active = controls.editIsActive.value;
    objectForUpdate._isEditMode = false;
    const updatePharmacyGroupDrug: Update<PharmacyGroupDrug> = {
      id: item.id,
      changes: objectForUpdate,
    };
    this.store.dispatch(
      new PharmacyGroupDrugOnServerUpdated({
        pharmacyGroupId: this.pharmacyGroupId,
        partialPharmacyGroupDrug: updatePharmacyGroupDrug,
        pharmacyGroupDrug: objectForUpdate,
      }),
    );
    this.isSwitchedToEditMode = false;
    this.loadingAfterSubmit = false;
  }

  /** UI */
  /**
   * Retursn CSS Class Name by status
   *
   * @param is_active: boolean
   */
  getItemCssClassByStatus(is_active = false): string {
    switch (is_active) {
      case false:
        return 'danger';
      case true:
        return 'success';
    }
  }

  /**
   * Returns Item Status in string
   * @param is_active: boolean
   */
  getItemStatusString(is_active = false): string {
    switch (is_active) {
      case false:
        return this.translate.instant('GLOBAL.INACTIVE');
      case true:
        return this.translate.instant('GLOBAL.ACTIVE');
    }
  }
}
